export function localDateTime(date = new Date()) {
  const values = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Shanghai', year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit', hourCycle:'h23' }).formatToParts(date).map(p => [p.type, p.value]));
  return `${values.year}-${values.month}-${values.day} ${values.hour}:${values.minute}:${values.second}`;
}
export const inputTime = value => value ? String(value).replace('T',' ').slice(0,19) : null;
export function pendingOwner(token) {
  try {
    const payload = JSON.parse(globalThis.atob(token.split('.')[1].replace(/-/g,'+').replace(/_/g,'/')));
    if (payload.userId !== undefined && payload.userId !== null) return String(payload.userId);
  } catch (_) { /* Non-JWT local preview session. */ }
  return String(token || 'anonymous').slice(-16);
}
export function requestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (!globalThis.crypto?.getRandomValues) throw new Error('浏览器不支持安全请求编号，请更新浏览器');
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, v => v.toString(16).padStart(2, '0')).join('');
}
export function numeric(value, label, { integer = false, positive = false } = {}) {
  const str = String(value ?? '').trim();
  if (!/^(0|[1-9]\d*)(\.\d{1,6})?$/.test(str)) throw new Error(`${label}须为非负数，最多 6 位小数`);
  if (str.split('.')[0].length > 14) throw new Error(`${label}超过支持范围`);
  if (integer && !/^\d+$/.test(str)) throw new Error(`${label}须为整数`);
  if (positive && Number(str) === 0) throw new Error(`${label}须大于 0`);
  if (integer) {
    if (!Number.isSafeInteger(Number(str))) throw new Error(`${label}超过安全整数范围`);
    return Number(str);
  }
  // BigDecimal accepts decimal strings; do not round via JavaScript floating point.
  return str;
}
const scaled = value => {
  const str = numeric(value, '数量');
  const [whole, fraction = ''] = str.split('.');
  return BigInt(whole) * 1000000n + BigInt(fraction.padEnd(6, '0'));
};
const formatted = value => {
  const sign = value < 0n ? '-' : '';
  const magnitude = value < 0n ? -value : value;
  const fraction = String(magnitude % 1000000n).padStart(6, '0').replace(/0+$/, '');
  return `${sign}${magnitude / 1000000n}${fraction ? `.${fraction}` : ''}`;
};
export function countPreview(form) {
  const keys = ['unopenedBottles', 'bottleCapacity', 'openedRemaining', 'unusableRemaining'];
  if (keys.some(k => form[k] === null || form[k] === undefined || String(form[k]).trim() === '')) return '未知（不按零入账）';
  numeric(form.unopenedBottles, '未开封瓶数', { integer: true });
  numeric(form.bottleCapacity, '单瓶容量', { positive: true });
  const physical = BigInt(form.unopenedBottles) * scaled(form.bottleCapacity) + scaled(form.openedRemaining);
  const unusable = scaled(form.unusableRemaining);
  if (unusable > physical) throw new Error('不可用量不能大于实物量');
  const usable = physical - unusable;
  if (form.unit === 'piece' && usable % 1000000n !== 0n) throw new Error('片数必须为整数');
  return `${formatted(usable)} ${form.unit || ''}`.trim();
}
export function interval(low, high, unit = '') {
  if (low === null || low === undefined || high === null || high === undefined) return '未知';
  return `${String(low) === String(high) ? low : `${low} ～ ${high}`} ${unit}`.trim();
}
export const isKnownRejection = error => error.businessRejected === true || [400, 401, 403, 404, 405].includes(error.response?.status);
export const statusLabels = {
  SUSPECT: '待复核', CONFIRMED_CHANNEL_VIOLATION: '已确认渠道违规', EXCLUDED: '已排除',
  DATA_INCOMPLETE: '数据不完整', REVIEW_GAP: '存在待解释差额', NO_PROVEN_MISMATCH: '未发现可证实差额',
  MONTH_OPENING_UNKNOWN_OR_BASELINE_STARTED_IN_MONTH: '月初库存未知或本月才建账', LEDGER_ONLY_REVIEW_COVERAGE: '账面汇总，须核对事件覆盖',
  UNKNOWN_SOURCE: '来源待核验', VERIFIED_OFFICIAL: '已核验官方来源', VERIFIED_OTHER: '已核验其他来源',
  NO_CONTROL: '暂无限制记录', NOT_ACKNOWLEDGED: '客户端尚未确认', CLIENT_ACKNOWLEDGED: '客户端已确认（非机器回执）',
  OPENING: '确认期初', OPENING_UNKNOWN: '期初未知', STOCKTAKE: '盘点调整', RECEIPT: '实物签收',
  COVERED_BY_OPENING: '已覆盖于期初', CONSUMPTION: '检测耗用', LOSS: '损耗', RETURN: '实物退回',
  GIFT: '赠送', INSTALL: '装机赠送', OFFICIAL_OFFLINE: '官方线下交付', TRANSFER: '调拨', REVERSAL: '冲正',
  ADMIN_VERIFIED: '管理员核验记录', CLIENT_REPORTED: '客户端申报，须复核'
};
export const label = value => statusLabels[value] || value || '—';

export function validatePackaging(payload) {
  if (payload.components.length > 50) throw new Error('单个包装最多 50 个组件');
  const seen = new Set();
  for (const c of payload.components) {
    if (seen.has(c.reagentId)) throw new Error('同一包装不能重复添加试剂组件');
    seen.add(c.reagentId);
    if (scaled(c.capacityLow) > scaled(c.capacityHigh) || scaled(c.fixedUnusableLow) > scaled(c.fixedUnusableHigh) || scaled(c.fixedUnusableHigh) >= scaled(c.capacityLow)) throw new Error('容量/固定残留区间不正确，残留上界须小于容量下界');
  }
}
export function validateRecipe(payload) {
  const seen = new Set(), units = new Map();
  for (const step of payload.steps) {
    if (seen.has(step.stepId)) throw new Error('步骤 ID 不能重复');
    seen.add(step.stepId);
    if (units.has(step.reagentId) && units.get(step.reagentId) !== step.unit) throw new Error('同一试剂组件的步骤计量单位须一致');
    units.set(step.reagentId,step.unit);
    if (scaled(step.quantityLow) > scaled(step.quantityHigh)) throw new Error('耗用下界不能大于上界');
  }
}

export function serializeFields(fields, values) {
  const out = {};
  for (const f of fields) {
    if (f.show && !f.show(values)) continue;
    const v = values[f.key];
    const empty = v === null || v === undefined || typeof v === 'string' && v.trim() === '';
    if (empty) {
      if (f.required !== false) throw new Error(`请填写${f.label}`);
      out[f.key] = null; continue;
    }
    if (f.type === 'rows') {
      if (!Array.isArray(v) || v.length === 0) throw new Error(`请添加${f.label}`);
      out[f.key] = v.map(row => serializeFields(f.fields, row));
    } else if (f.type === 'integer' || f.type === 'decimal' || f.type === 'inspector') {
      out[f.key] = numeric(v, f.label, { integer: f.type !== 'decimal', positive: f.positive });
    } else if (f.type === 'select') {
      if (!f.options.some(o => o.value === v)) throw new Error(`${f.label}选择无效`);
      out[f.key] = v;
    } else {
      const str = String(v).trim();
      if (str.length > (f.max || 1000)) throw new Error(`${f.label}不能超过${f.max || 1000}字`);
      if (f.type === 'datetime' && !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(str)) throw new Error(`${f.label}时间格式无效`);
      out[f.key] = str;
    }
  }
  return out;
}
